// Display-unit settings for the editor UI: px (raw raster pixels) or mm
// (physical size at a print resolution). PERSISTENCE BUG MODEL: only the
// DISPLAY unit and PPI are stored — every pixel value the app computes is
// untouched, so switching units can never corrupt separation data.

import { useMemo, useState } from 'react';
import type { Unit } from '@imagelayerdecomposer/core';

const UNIT_KEY = 'colorsep_unit';
const PPI_KEY = 'colorsep_print_ppi';

/** Textile design default: 150 dpi ⇒ 3000 px = 50.8 cm wide at print size. */
export const DEFAULT_PPI = 150;

export type MeasureSettings = {
  unit: Unit;
  ppi: number;
  setUnit: (u: Unit) => void;
  setPpi: (ppi: number) => void;
};

function readStoredUnit(): Unit {
  try {
    return localStorage.getItem(UNIT_KEY) === 'px' ? 'px' : 'mm';
  } catch {
    return 'mm';
  }
}

function sanitizePpi(ppi: number): number {
  return Number.isFinite(ppi) && ppi > 0 && ppi <= 100_000 ? Math.round(ppi) : DEFAULT_PPI;
}

function readStoredPpi(): number {
  try {
    return sanitizePpi(Number(localStorage.getItem(PPI_KEY)));
  } catch {
    return DEFAULT_PPI;
  }
}

export function useMeasure(): MeasureSettings {
  const [unit, setUnitState] = useState<Unit>(readStoredUnit);
  const [ppi, setPpiState] = useState<number>(readStoredPpi);

  return useMemo<MeasureSettings>(
    () => ({
      unit,
      ppi,
      setUnit: (u) => {
        setUnitState(u);
        try {
          localStorage.setItem(UNIT_KEY, u);
        } catch {
          /* private mode — session-only setting */
        }
      },
      setPpi: (p) => {
        const next = sanitizePpi(p);
        setPpiState(next);
        try {
          localStorage.setItem(PPI_KEY, String(next));
        } catch {
          /* private mode — session-only setting */
        }
      },
    }),
    [unit, ppi],
  );
}
